import { APP_CONFIG } from '../config/appConfig';

/** Fixed locale for Intl time formatting (24h). Not a user preference. */
export const DISPLAY_TIME_LOCALE = 'en-GB';

export const USER_DATE_FORMAT_OPTIONS = [
  { value: '', label: 'Default (DD/MM/YYYY)' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
  { value: 'DD.MM.YYYY', label: 'DD.MM.YYYY' },
  { value: 'DD-MM-YYYY', label: 'DD-MM-YYYY' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
  { value: 'YYYY/MM/DD', label: 'YYYY/MM/DD' },
] as const;

export type UserDateFormatValue = Exclude<
  (typeof USER_DATE_FORMAT_OPTIONS)[number]['value'],
  ''
>;

export const USER_DATE_FORMAT_VALUES = USER_DATE_FORMAT_OPTIONS.map((o) => o.value).filter(
  (v): v is UserDateFormatValue => v.length > 0,
);

export type UserTimeFormat = '24h' | '12h';

export const USER_TIME_FORMAT_OPTIONS = [
  { value: '', label: 'Default (24-hour)' },
  { value: '24h', label: '24-hour' },
  { value: '12h', label: '12-hour' },
] as const;

export const DEFAULT_TIME_FORMAT: UserTimeFormat = '24h';

export interface UserDatePreferences {
  dateFormat?: string | null;
  timeFormat?: string | null;
}

let preferences: UserDatePreferences = {};

export function setUserDatePreferences(prefs: UserDatePreferences): void {
  preferences = {
    dateFormat: prefs.dateFormat ?? null,
    timeFormat: prefs.timeFormat ?? null,
  };
}

export function clearUserDatePreferences(): void {
  preferences = {};
}

export function getUserDatePreferences(): Readonly<UserDatePreferences> {
  return preferences;
}

export function resolveUserDateFormat(override?: string): string {
  const value = override?.trim() || preferences.dateFormat?.trim();
  return value || APP_CONFIG.dateFormat;
}

export function resolveUserTimeFormat(override?: string): UserTimeFormat {
  const value = override?.trim() || preferences.timeFormat?.trim();
  return value === '12h' ? '12h' : DEFAULT_TIME_FORMAT;
}

export interface FormatDisplayTimeOptions {
  timeZone?: string;
  timeFormat?: string;
}

export function formatDisplayTime(date: Date, options?: FormatDisplayTimeOptions): string {
  const tzOpts = options?.timeZone ? { timeZone: options.timeZone } : {};
  const hour12 = resolveUserTimeFormat(options?.timeFormat) === '12h';

  return date.toLocaleTimeString(DISPLAY_TIME_LOCALE, {
    hour: '2-digit',
    minute: '2-digit',
    hour12,
    ...tzOpts,
  });
}

export type DatePartOrder = 'DMY' | 'MDY' | 'YMD';

export function dateFormatToPartOrder(format: string): DatePartOrder {
  const normalized = format.toUpperCase().replace(/\./g, '/');
  if (normalized.startsWith('YYYY')) return 'YMD';
  if (normalized.startsWith('MM') || normalized.startsWith('M/')) return 'MDY';
  return 'DMY';
}

export function getDateSeparator(format: string): string {
  if (format.includes('-')) return '-';
  if (format.includes('.')) return '.';
  return '/';
}

function calendarParts(
  date: Date,
  timeZone?: string,
): { day: string; month: string; year: string } {
  const parts = new Intl.DateTimeFormat(DISPLAY_TIME_LOCALE, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...(timeZone ? { timeZone } : {}),
  }).formatToParts(date);

  return {
    day: parts.find((part) => part.type === 'day')?.value ?? '01',
    month: parts.find((part) => part.type === 'month')?.value ?? '01',
    year: parts.find((part) => part.type === 'year')?.value ?? '1970',
  };
}

export function formatDateWithPattern(
  date: Date,
  format: string,
  timeZone?: string,
): string {
  const order = dateFormatToPartOrder(format);
  const separator = getDateSeparator(format);
  const { day, month, year } = calendarParts(date, timeZone);

  if (order === 'YMD') return `${year}${separator}${month}${separator}${day}`;
  if (order === 'MDY') return `${month}${separator}${day}${separator}${year}`;
  return `${day}${separator}${month}${separator}${year}`;
}

export interface FormatDisplayDateOptions {
  dateFormat?: string;
  timeZone?: string;
  timeFormat?: string;
}

export function formatDisplayDate(
  date: Date,
  options?: FormatDisplayDateOptions,
): string {
  const format = resolveUserDateFormat(options?.dateFormat);
  return formatDateWithPattern(date, format, options?.timeZone);
}
